import React from 'react';
import {
  User, CheckCircle,
  Building2, Briefcase, MapPin, Mail, Phone, Globe,
} from 'lucide-react';
import { fileUrl } from '../../api/client';
import { useLanguage } from '../../context/LanguageContext';
import './ProfileView.css';

function chip(label, key) {
  return (
    <span className="tp-chip" key={key}>
      {label}
    </span>
  );
}

const CLIENT_TYPE_LABELS = {
  particulier: 'Particulier',
  entreprise: 'Entreprise',
  organisation: 'Organisation',
};

const AVAILABILITY_LABELS = {
  available: 'Disponible',
  unavailable: 'Indisponible',
  suspended: 'Suspendu',
  disponible: 'Disponible',
  partiellement_disponible: 'Partiellement disponible',
  indisponible: 'Indisponible',
};

function val(v) {
  if (v == null || v === '') return '—';
  if (typeof v === 'boolean') return v ? 'Oui' : 'Non';
  return v;
}

function langName(code, t) {
  return t?.quoteForm?.languages?.[code] || code;
}

function countryName(code, t) {
  return t?.quoteForm?.countries?.[code] || code;
}

export default function ProfileView({ user, space }) {
  const { t } = useLanguage();
  const u = user || {};
  const isClient = space === 'client';
  const isLegal = ['legal', 'lawyer', 'notary'].includes(space);
  const isAdmin = space === 'admin';
  const vp = u.verifierProfile || {};

  const civility = u.civility || vp.civility || '';
  const baseName = u.name || [u.firstName, u.lastName].filter(Boolean).join(' ') || '—';
  const name = civility ? `${civility} ${baseName}` : baseName;
  const photo = fileUrl(u.photoUrl || vp.photoUrl);

  const city = vp.city || u.city;
  const region = vp.region || u.region || (u.translatorProfile && u.translatorProfile.region) || '';
  const country = vp.country || u.country;

  const role = isClient
    ? (CLIENT_TYPE_LABELS[u.clientType] || 'Client')
    : isAdmin
    ? 'Administrateur'
    : (space === 'lawyer'
      ? 'Avocat'
      : space === 'notary'
      ? 'Notaire'
      : 'Vérificateur juridique');

  const isActive = u.isActive !== false;
  const statusLabel = isActive ? 'Actif' : 'Inactif';
  const availability = isLegal
    ? (AVAILABILITY_LABELS[vp.availability || u.availability] || vp.availability || u.availability || '—')
    : null;

  const languages = isClient
    ? [].concat(u.languages || u.languageCombos || [])
    : [].concat(vp.languages || u.languages || []);

  const specialties = (vp.specialties || u.specialties || []).filter(Boolean);
  const verificationTypes = (vp.verificationTypes || u.verificationTypes || []).filter(Boolean);

  return (
    <div className="profile-view">
      <aside className="tp-sidebar">
        <div className="tp-avatar">
          {photo ? (
            <img src={photo} alt={name} />
          ) : (
            <div className="tp-avatar-placeholder"><User size={56} strokeWidth={1.6} /></div>
          )}
        </div>

        <div className="tp-sidebar-block">
          <div className="tp-list">
            {(vp.professionalEmail || u.email) && (
              <a className="tp-list-item" href={`mailto:${vp.professionalEmail || u.email}`}>
                <Mail size={14} /> {vp.professionalEmail || u.email}
              </a>
            )}
            {(vp.phone || u.phone) && (
              <a className="tp-list-item" href={`tel:${vp.phone || u.phone}`}>
                <Phone size={14} /> {vp.phone || u.phone}
              </a>
            )}
            {vp.website && (
              <a className="tp-list-item" href={vp.website} target="_blank" rel="noreferrer">
                <Globe size={14} /> {vp.website}
              </a>
            )}
            {!isClient && (
              <div className="tp-list-item tp-list-item--muted">
                <MapPin size={14} />
                {[region, city, countryName(country, t)].filter(Boolean).join(', ') || '—'}
              </div>
            )}
          </div>
        </div>

        {languages.length > 0 && (
          <div className="tp-sidebar-block">
            <h3>Langues</h3>
            <div className="tp-chips">
              {languages.map((c, i) =>
                chip(typeof c === 'string'
                  ? langName(c, t)
                  : isLegal
                    ? langName(c.language, t)
                    : `${langName(c.source, t)} → ${langName(c.target, t)}`, `lang-${i}`)
              )}
            </div>
          </div>
        )}
      </aside>

      <main className="tp-main">
        <div className="tp-hero">
          <div>
            <h1 className="tp-name">{name}</h1>
            <p className="tp-role">{role}</p>
          </div>
          <div className="tp-badges">
            {isLegal ? (
              <span className={`tp-badge tp-badge--${(vp.availability || u.availability) === 'available' || (vp.availability || u.availability) === 'disponible' ? 'ok' : 'busy'}`}>
                {availability}
              </span>
            ) : (
              <span className={`tp-badge tp-badge--${isActive ? 'ok' : 'busy'}`}>
                {statusLabel}
              </span>
            )}
          </div>
        </div>

        <div className="tp-grid">
          <section className="tp-card tp-card--wide">
            <header className="tp-card-head">
              <User size={16} />
              <h2>Informations personnelles</h2>
            </header>
            <div className="tp-meta">
              <div>
                <strong>Civilité</strong>
                <span>{val(civility)}</span>
              </div>
              <div>
                <strong>Nom</strong>
                <span>{name}</span>
              </div>
            </div>
          </section>

          {isClient && (
            <section className="tp-card tp-card--wide">
              <header className="tp-card-head">
                <Building2 size={16} />
                <h2>Détails client</h2>
              </header>
              <div className="tp-meta">
                <div>
                  <strong>Type de client</strong>
                  <span>{CLIENT_TYPE_LABELS[u.clientType] || val(u.clientType)}</span>
                </div>
                {u.clientType !== 'particulier' && (
                  <>
                    <div>
                      <strong>Organisation</strong>
                      <span>{val(u.organizationName)}</span>
                    </div>
                    <div>
                      <strong>Identifiant fiscal</strong>
                      <span>{val(u.taxId)}</span>
                    </div>
                  </>
                )}
              </div>
            </section>
          )}

          {isLegal && (
            <section className="tp-card tp-card--wide">
              <header className="tp-card-head">
                <Briefcase size={16} />
                <h2>Informations professionnelles</h2>
              </header>
              <div className="tp-meta">
                <div>
                  <strong>Cabinet / Étude</strong>
                  <span>{val(vp.firm || u.firm)}</span>
                </div>
                <div>
                  <strong>Numéro professionnel</strong>
                  <span>{val(vp.professionalNumber)}</span>
                </div>
                <div>
                  <strong>Barreau / Organisme</strong>
                  <span>{val(vp.professionalBody)}</span>
                </div>
                <div>
                  <strong>Email professionnel</strong>
                  <span>{val(vp.professionalEmail)}</span>
                </div>
                <div>
                  <strong>Années d'expérience</strong>
                  <span>{vp.experienceYears ? `${vp.experienceYears} ans` : '—'}</span>
                </div>
                <div>
                  <strong>Mode d'intervention</strong>
                  <span>{val(vp.interventionMode)}</span>
                </div>
                <div>
                  <strong>Disponibilité</strong>
                  <span>{availability}</span>
                </div>
              </div>

              <div style={{ marginTop: 14 }}>
                <h4 className="tp-subtitle-label">Spécialités</h4>
                {specialties.length ? (
                  <div className="tp-chips">{specialties.map((s, i) => chip(s, `spec-${i}`))}</div>
                ) : (
                  <p className="tp-empty">—</p>
                )}
              </div>

              <div style={{ marginTop: 14 }}>
                <h4 className="tp-subtitle-label">Types de vérification</h4>
                {verificationTypes.length ? (
                  <div className="tp-chips">{verificationTypes.map((s, i) => chip(s, `vt-${i}`))}</div>
                ) : (
                  <p className="tp-empty">—</p>
                )}
              </div>

              {languages.length > 0 && (
                <div style={{ marginTop: 14 }}>
                  <h4 className="tp-subtitle-label">Langues</h4>
                  <div className="tp-chips">
                    {languages.map((c, i) =>
                      chip(typeof c === 'string'
                        ? langName(c, t)
                        : isLegal
                          ? langName(c.language, t)
                          : `${langName(c.source, t)} → ${langName(c.target, t)}`, `lang-main-${i}`)
                    )}
                  </div>
                </div>
              )}
            </section>
          )}

          {isAdmin && (
            <section className="tp-card tp-card--wide">
              <header className="tp-card-head">
                <CheckCircle size={16} />
                <h2>Rôle</h2>
              </header>
              <div className="tp-meta">
                <div>
                  <strong>Fonction</strong>
                  <span>{role}</span>
                </div>
              </div>
            </section>
          )}
        </div>
      </main>
    </div>
  );
}
